import { useState } from 'react';
import { ContentCard } from '../../../components/ContentCard/ContentCard';
import { Button } from '../../../components/Form/Button';
import { Icon, close } from '../../../components/Icon/Icon';
import { Modal } from '../../../components/Modal/Modal';
import { useAddProductSearch } from '../../../hooks/useAddProductSearch';
import { useTranslation } from '../../../i18n/i18n';
import { Res, useApi } from '../../../state/api';
import { useSession } from '../../../state/session';
import { ApiCartItem, ApiOrderItem } from '../../../types';
import { AlertCollection, AlertMessages } from '../../AlertMessages/AlertMessages';
import { WizardStepLabel } from '../../subscriptions/WizardStepLabel';
import { WizardControl } from '../../subscriptions/wizard';
import './AddProductModal.css';
import { AddProductSearchForm } from './AddProductSearchForm';
import { ProductListing } from './ProductListing';
import { ProductSource, ProductSourceTiles } from './ProductSourceTiles';

interface AddProductModalProps {
	onSave: (...args: any[]) => void;
	title?: string;
	deleting?: boolean;
	/* a caller whose save can fail passes its alerts, so the message lands over the modal and not under it */
	alerts?: AlertCollection;
	/* set while the wizard is on its last step, which is also what holds the modal open */
	wizard?: WizardControl;
	/* the draft card has a button for this of its own, and does not want a second one */
	dialogOnly?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const AddProductModal = ({
	onSave,
	title,
	deleting,
	alerts,
	wizard,
	dialogOnly,
}: AddProductModalProps): JSX.Element => {
	const [open, setOpen] = useState(false);
	const [source, setSource] = useState<ProductSource | null>(null);
	const t = {
		...useTranslation().editOrder,
		generic: useTranslation().generic,
		wizardLabels: useTranslation().subscriptions.wizard,
	};
	const inWizard = !!wizard;
	/* the wizard's last step is over when the customer says so: there is nothing here to save */
	const closeModal = () => (wizard ? wizard.onAbandon() : setOpen(false));
	const { results, query, setQuery, loading, setPage, areMoreResults } = useAddProductSearch();
	const { isLoggedIn } = useSession();
	// nothing is fetched until the modal is opened, since both lists are only ever seen inside it
	const { data: favorites } = useApi((open || inWizard) && isLoggedIn ? Res.favorites : null, {});
	const { data: mostBought } = useApi((open || inWizard) && isLoggedIn ? Res.mostBought : null, {});
	const addProduct = (item: ApiOrderItem | ApiCartItem): void => onSave(item);
	const bodyClassName = ['modal-body', loading && 'loading'].join(' ');

	/* a list is offered only when the customer has one, which is also why the row can be empty */
	const available: ProductSource[] = [
		...(favorites?.length ? (['favorites'] as const) : []),
		...(mostBought?.length ? (['mostBought'] as const) : []),
	];
	const searching = !!query;
	/* unstarring the last favourite from inside the modal takes the list away under the selection */
	const selected = source && available.includes(source) ? source : null;
	const sourceProducts = selected === 'favorites' ? favorites : selected === 'mostBought' ? mostBought : [];
	// the search wins while there is something in the box; clearing it hands the list back
	const products = searching ? results?.items ?? [] : sourceProducts ?? [];

	return (
		<>
			{!dialogOnly && (
				<Button className="add-product-button" disabled={deleting} onClick={() => setOpen(true)}>
					{t.addProduct}
				</Button>
			)}

			<Modal
				className="add-product-modal"
				show={open || inWizard}
				disableBodyScroll
				onDismiss={closeModal}
				toggle={() => (open || inWizard ? closeModal() : setOpen(true))}
			>
				<ContentCard className="add-product-content">
					<div className="modal-header">
						<div className="modal-header-title">
							<WizardStepLabel step={wizard?.step} />
							{title}
						</div>
						<button className="close-btn" onClick={closeModal}>
							<Icon shape={{ ...close, fill: 'black' }} size={20} />
						</button>
					</div>
					<div className={bodyClassName}>
						{alerts && <AlertMessages alerts={alerts} />}
						<AddProductSearchForm query={query} setQuery={setQuery} results={results} />
						{!searching && available.length > 0 && (
							<ProductSourceTiles available={available} selected={selected} onSelect={setSource} />
						)}
						{/*
						 * An empty body says why it is empty: what to do before anything is chosen,
						 * that a search is on its way, or that the search came back with nothing.
						 */}
						{products.length === 0 && (
							<p className="product-listing-hint">
								{loading
									? t.fetchingProducts
									: searching && results.showResults
										? t.noProductsFound
										: t.searchOrPick}
							</p>
						)}
						<ProductListing onChange={addProduct} products={products} />
						<div className="search-footer">
							{searching && areMoreResults && results?.items && (
								<Button
									className="big"
									disabled={loading}
									onClick={() => {
										setPage((prev) => prev + 1);
									}}
								>
									{loading ? t.generic.loading : t.generic.loadMore}
								</Button>
							)}
						</div>
						{wizard && (
							<div className="wizard-footer">
								{/* saying "done" with nothing added would hand back a subscription that sends nothing */}
								<Button disabled={!wizard.ready} onClick={wizard.onDone}>
									{t.wizardLabels.done}
								</Button>
							</div>
						)}
					</div>
				</ContentCard>
			</Modal>
		</>
	);
};
